/**
 * Cookie Consent Cookie Helpers
 *
 * Read/write helpers for the three first-party cookies owned by the consent
 * feature:
 *
 * - `as_cookie_consent` — the decision: `{ categories: { necessary,
 *   analytics, advertising }, version, gpc, origin }`, where `origin` is the
 *   hostname that wrote it. Shared with the marketing site.
 * - `as_anonymous_id` — UUIDv4 used to correlate anonymous decisions with a
 *   subsequent signed-in user. Shared with the marketing site.
 * - `as_consent_synced` — host-only marker holding the `CONSENT_VERSION` a
 *   cross-origin cookie was last recorded against on this host.
 *
 * Domain rule (D14): on `activesnap.ai` and any of its subdomains the two
 * shared cookies carry `Domain=.activesnap.ai` so both subdomains read one
 * decision; everywhere else (localhost, test hosts) they are host-only. The
 * synced marker is always host-only. All three are `SameSite=Lax`, live for
 * 12 months, and are `Secure` on https.
 *
 * Parser rule (D16): a consent cookie is only a decision when it carries the
 * current version, `necessary: true` and boolean `analytics` and
 * `advertising`; anything else reads as "no decision yet". A missing
 * `origin` reads as `''`.
 *
 * These helpers are framework-agnostic: they only touch `document.cookie`
 * so they can run from the vanilla Vite bundle before React boots.
 */

import { CONSENT_VERSION } from "./version";
import type { ConsentCookie } from "./types";

export const CONSENT_COOKIE = "as_cookie_consent";
export const ANONYMOUS_ID_COOKIE = "as_anonymous_id";
export const CONSENT_SYNCED_COOKIE = "as_consent_synced";
export const PARENT_DOMAIN_SUFFIX = "activesnap.ai";
export const TWELVE_MONTHS_SECONDS = 60 * 60 * 24 * 365;

/**
 * Shape persisted inside the `as_cookie_consent` cookie.
 */
export type ConsentCookieValue = ConsentCookie;

/**
 * Whether the page runs on the `activesnap.ai` apex or one of its
 * subdomains (copied from the website's `isProductionHost`).
 *
 * @param hostname - `location.hostname` of the page
 * @returns `true` for `activesnap.ai` and `*.activesnap.ai`, else `false`
 */
export function isProductionHost(hostname: string): boolean {
  return (
    hostname === PARENT_DOMAIN_SUFFIX ||
    hostname.endsWith(`.${PARENT_DOMAIN_SUFFIX}`)
  );
}

/**
 * Attributes for a host-only 12-month cookie.
 *
 * @param protocol - `location.protocol` of the page (`"https:"` adds `Secure`)
 * @returns e.g. `"Path=/; Max-Age=31536000; SameSite=Lax; Secure"`
 */
export function buildHostOnlyCookieAttributes(protocol: string): string {
  const parts = [`Path=/`, `Max-Age=${TWELVE_MONTHS_SECONDS}`, `SameSite=Lax`];
  if (protocol === "https:") parts.push("Secure");
  return parts.join("; ");
}

/**
 * Attributes for the two cookies shared with the marketing site, in the
 * website's order: `Domain=.activesnap.ai` on production hosts, then
 * `Secure` on https.
 *
 * @param hostname - `location.hostname` of the page
 * @param protocol - `location.protocol` of the page
 * @returns the attribute string appended after `name=value; `
 */
export function buildCookieAttributes(
  hostname: string,
  protocol: string,
): string {
  const parts = [`Path=/`, `Max-Age=${TWELVE_MONTHS_SECONDS}`, `SameSite=Lax`];
  if (isProductionHost(hostname)) {
    parts.push(`Domain=.${PARENT_DOMAIN_SUFFIX}`);
  }
  if (protocol === "https:") {
    parts.push("Secure");
  }
  return parts.join("; ");
}

/**
 * Parse a raw (URL-encoded JSON) consent cookie value (website logic).
 *
 * @param raw - the raw cookie value, or `null` when absent
 * @returns the normalised cookie, or `null` when it is missing, unparseable,
 *   on a different version, or not a valid three-key decision
 */
export function parseConsentCookie(raw: string | null): ConsentCookie | null {
  const obj = parseRawObject(raw);
  if (!obj) return null;
  const categories = obj.categories as
    | { necessary?: unknown; analytics?: unknown; advertising?: unknown }
    | undefined;
  if (!categories || typeof obj.version !== "string") return null;
  if (categories.necessary !== true) return null;
  if (typeof categories.analytics !== "boolean") return null;
  if (typeof categories.advertising !== "boolean") return null;
  if (obj.version !== CONSENT_VERSION) return null;
  return {
    categories: {
      necessary: true,
      analytics: categories.analytics,
      advertising: categories.advertising,
    },
    version: obj.version,
    gpc: obj.gpc === true,
    origin: typeof obj.origin === "string" ? obj.origin : "",
  };
}

/**
 * Read and parse the consent cookie.
 *
 * @returns the parsed cookie value, or `null` when the cookie is missing
 *   or is not a valid current-version decision (treated as "no decision yet").
 */
export function readConsentCookie(): ConsentCookieValue | null {
  return parseConsentCookie(readRawCookie(CONSENT_COOKIE));
}

/**
 * Read the `version` of any parseable consent cookie, whatever its shape.
 * Lets the core tell a stale-version cookie (re-prompt) from no cookie.
 *
 * @returns the stored version string, or `null` when absent or unparseable
 */
export function readConsentCookieVersion(): string | null {
  const obj = parseRawObject(readRawCookie(CONSENT_COOKIE));
  if (!obj || typeof obj.version !== "string") return null;
  return obj.version;
}

/**
 * Persist the consent decision into the `as_cookie_consent` cookie, stamped
 * with the current hostname as `origin`.
 *
 * @param categories - the selection for this decision; `advertising` is
 *   normalised to `=== true` (absent means off)
 * @param version - the `CONSENT_VERSION` value that the decision was made against
 * @param gpc - whether Global Privacy Control was detected at decision time
 */
export function writeConsentCookie(
  categories: {
    necessary?: boolean;
    analytics: boolean;
    advertising?: boolean;
  },
  version: string,
  gpc: boolean,
): void {
  const env = currentEnv();
  const payload: ConsentCookieValue = {
    categories: {
      necessary: true,
      analytics: categories.analytics === true,
      advertising: categories.advertising === true,
    },
    version,
    gpc,
    origin: env.hostname,
  };
  writeRawCookie(
    CONSENT_COOKIE,
    encodeURIComponent(JSON.stringify(payload)),
    buildCookieAttributes(env.hostname, env.protocol),
  );
}

/**
 * Read the host-only `as_consent_synced` marker.
 *
 * @returns the `CONSENT_VERSION` the marker was written for, or `null`
 */
export function readConsentSyncedMarker(): string | null {
  const raw = readRawCookie(CONSENT_SYNCED_COOKIE);
  if (!raw) return null;
  try {
    return decodeURIComponent(raw);
  } catch {
    return null;
  }
}

/**
 * Write the host-only `as_consent_synced` marker (never carries a Domain).
 *
 * @param version - the `CONSENT_VERSION` the cross-origin cookie was recorded against
 */
export function writeConsentSyncedMarker(version: string): void {
  writeRawCookie(
    CONSENT_SYNCED_COOKIE,
    encodeURIComponent(version),
    buildHostOnlyCookieAttributes(currentEnv().protocol),
  );
}

/**
 * Read the `as_anonymous_id` cookie value without creating one.
 *
 * @returns the UUID stored in the cookie, or `null` when absent
 */
export function readAnonymousId(): string | null {
  return readRawCookie(ANONYMOUS_ID_COOKIE);
}

/**
 * Return the anonymous id, minting and persisting a fresh UUIDv4 when
 * none is present. The server-side public consents controller also mints
 * and sets this cookie - calling `ensureAnonymousId()` client-side lets us
 * correlate the first pre-API decision with the row about to be created.
 *
 * @returns the (existing or newly minted) anonymous id
 */
export function ensureAnonymousId(): string {
  const existing = readAnonymousId();
  if (existing) return existing;

  const fresh = generateUuidV4();
  const env = currentEnv();
  writeRawCookie(
    ANONYMOUS_ID_COOKIE,
    fresh,
    buildCookieAttributes(env.hostname, env.protocol),
  );
  return fresh;
}

/**
 * Low-level cookie reader. Splits `document.cookie` and returns the raw
 * value for the named cookie, or `null` when absent.
 *
 * @param name - cookie name to look up
 * @returns the raw cookie value, or `null`
 */
export function readRawCookie(name: string): string | null {
  if (typeof document === "undefined" || !document.cookie) return null;

  const prefix = `${name}=`;
  const parts = document.cookie.split(";");
  for (const part of parts) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      return trimmed.slice(prefix.length);
    }
  }
  return null;
}

/**
 * Low-level cookie writer.
 *
 * @param name - cookie name
 * @param value - already-encoded cookie value (no URL encoding performed here)
 * @param attributes - attribute string from `buildCookieAttributes` or
 *   `buildHostOnlyCookieAttributes`
 */
function writeRawCookie(name: string, value: string, attributes: string): void {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=${value}; ${attributes}`;
}

/**
 * Decode a raw cookie value into a plain object.
 *
 * @param raw - URL-encoded JSON, or `null`
 * @returns the decoded object, or `null` when absent or not an object
 */
function parseRawObject(raw: string | null): Record<string, unknown> | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(decodeURIComponent(raw));
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object") return null;
  return parsed as Record<string, unknown>;
}

/**
 * The page's hostname and protocol; SSR-safe.
 *
 * @returns `{ hostname, protocol }`, or `{ hostname: "", protocol: "http:" }`
 *   outside a browser
 */
function currentEnv(): { hostname: string; protocol: string } {
  if (typeof window === "undefined" || !window.location) {
    return { hostname: "", protocol: "http:" };
  }
  return {
    hostname: window.location.hostname ?? "",
    protocol: window.location.protocol ?? "http:",
  };
}

/**
 * Generate a RFC-4122 v4 UUID. Prefers `crypto.randomUUID()` when
 * available and falls back to a `crypto.getRandomValues`-backed
 * implementation for older browsers.
 *
 * @returns a fresh UUIDv4 string
 */
function generateUuidV4(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }

  const bytes = new Uint8Array(16);
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.getRandomValues === "function"
  ) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex: string[] = [];
  for (let i = 0; i < 16; i += 1)
    hex.push(bytes[i].toString(16).padStart(2, "0"));
  return (
    `${hex[0]}${hex[1]}${hex[2]}${hex[3]}-` +
    `${hex[4]}${hex[5]}-` +
    `${hex[6]}${hex[7]}-` +
    `${hex[8]}${hex[9]}-` +
    `${hex[10]}${hex[11]}${hex[12]}${hex[13]}${hex[14]}${hex[15]}`
  );
}
